import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeMode, themes } from '../theme';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'raporgor-theme';

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(() => {
    // localStorage'dan kayıtlı temayı al
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    // İlk giriş için varsayılan: light mode
    return 'light';
  });

  // Tema değiştiğinde CSS değişkenlerini uygula
  useEffect(() => {
    const theme = themes[mode];

    // CSS değişkenlerini :root'a uygula
    const style = document.documentElement.style;
    style.setProperty('--color-primary', theme.primary);
    style.setProperty('--color-primary-hover', theme.primaryHover);
    style.setProperty('--color-primary-light', theme.primaryLight);
    style.setProperty('--color-page-bg', theme.pageBg);
    style.setProperty('--color-card-bg', theme.cardBg);
    style.setProperty('--color-sidebar-bg', theme.sidebarBg);
    style.setProperty('--color-header-bg', theme.headerBg);
    style.setProperty('--color-hover-bg', theme.hoverBg);
    style.setProperty('--color-selected-bg', theme.selectedBg);
    style.setProperty('--color-accent-bg', theme.accentBg);
    style.setProperty('--color-input-bg', theme.inputBg);
    style.setProperty('--color-text-primary', theme.textPrimary);
    style.setProperty('--color-text-secondary', theme.textSecondary);
    style.setProperty('--color-text-muted', theme.textMuted);
    style.setProperty('--color-text-disabled', theme.textDisabled);
    style.setProperty('--color-text-inverse', theme.textInverse);
    style.setProperty('--color-border', theme.border);
    style.setProperty('--color-border-light', theme.borderLight);
    style.setProperty('--color-positive', theme.positive);
    style.setProperty('--color-negative', theme.negative);
    style.setProperty('--color-error', theme.error);
    style.setProperty('--color-error-bg', theme.errorBg);
    style.setProperty('--color-error-border', theme.errorBorder);
    style.setProperty('--color-success', theme.success);
    style.setProperty('--color-warning', theme.warning);
    style.setProperty('--shadow-color', theme.shadowColor);
    style.setProperty('--shadow-color-strong', theme.shadowColorStrong);

    // Body'ye tema class'ı ekle
    document.body.classList.remove('theme-light', 'theme-dark');
    document.body.classList.add(`theme-${mode}`);

    // localStorage'a kaydet
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, [mode]);

  // Sistem tema değişikliğini dinle
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      // Sadece localStorage'da kayıtlı tercih yoksa sistem tercihini uygula
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (!stored) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setMode(prev => prev === 'light' ? 'dark' : 'light');
  };

  const setTheme = (newMode: ThemeMode) => {
    setMode(newMode);
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme hook ThemeProvider içinde kullanılmalıdır');
  }
  return context;
}
